import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateServiceRequestTask, uploadTaskAndUpdate } from '../../../../actions/service_request';
import { SimpleButton } from '../../../../common/Buttons/SimpleButton';
import { motion } from 'framer-motion';
import RaiseSignOff from './RaiseSignoff';
import { MilestoneSubStatus, MilestoneSubStatusOrder, MilestoneType, TicketAgentRole, TicketStatus } from '../../../Ticket/TicketController';
import ReachedSite from './ReachedSite';
import TaskInputBar from './TaskInputBar';
import IconToolTip from '../../../../common/IconToolTip';
import PavAssetVerification from '../../../../features/pav/PavAssetVerification';
import { canRaisePavMilestoneSignOff, hasPavMilestoneAssets, isPavMilestone } from '../../../../features/pav/pavConfig';


const checkInRange = (status, lowerBound, upperBound) => {
    const statusOrder = MilestoneSubStatusOrder[status];
    const lowerOrder = lowerBound ? MilestoneSubStatusOrder[lowerBound] : -Infinity;
    const upperOrder = upperBound ? MilestoneSubStatusOrder[upperBound] : Infinity;
    return statusOrder < upperOrder && statusOrder >= lowerOrder;
}


const Checklist = ({ currentMilestone, id, setHeaderColor, memoizedServiceRequest, updateTaskInMilestone, milestoneTasksState }) => {
    const formName = 'reachedSite';
    const dispatch = useDispatch();
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const inviteFormImagesUrls = useSelector((state) => state.user.inviteFormImagesUrls);
    const [activeTaskIndex, setActiveTaskIndex] = useState(null);

    // Use shared task state instead of local state
    const tasks = useMemo(() => {
        return milestoneTasksState[currentMilestone?.id] || [];
    }, [milestoneTasksState, currentMilestone?.id]);

    const handleTaskClick = (index) => {
        if (!isTaskAlreadyUploaded(tasks[index]) && canEditChecklist) {
            if (tasks[index].input_type === 'FILE') {
                const fileInput = document.getElementById(`file-input-${tasks[index].id || index}`);
                if (fileInput) {
                    fileInput.click();
                }
            } else {
                setActiveTaskIndex(index);
            }
        }
    };

    const handleCloseInputBar = () => {
        setActiveTaskIndex(null);
    };

    // Remove the old local state initialization since we're using shared state now

    const handleInputChange = (index, value) => {
        const updatedTask = {
            responses: Array.isArray(value) ? value.map(option => ({ value: option, label: option })) : [{ value: value, label: value }],
            isSaved: false
        };
        updateTaskInMilestone(currentMilestone.id, index, updatedTask);
    };

    const handleMultiSelectChange = (index, selectedOptions) => {
        const values = selectedOptions.map(option => ({ value: option.value, label: option.label }));
        const updatedTask = {
            responses: values,
            isSaved: false
        };
        updateTaskInMilestone(currentMilestone.id, index, updatedTask);
    };

    const handleFileUpload = (index, event, task) => {
        const files = [...event.target.files];
        const updatedTask = {
            files: files,
            isSaved: false
        };
        updateTaskInMilestone(currentMilestone.id, index, updatedTask);

        dispatch(uploadTaskAndUpdate({
            formName: 'formname', payload: {
                task_id: task.id,
                ticket_id: id,
                files
            }
        }));
    };

    const handleSave = (index, task) => {
        const uploadedInviteFormImagesUrls = inviteFormImagesUrls[`${task.id}-media`];
        const updatedTask = { isSaved: true };
        updateTaskInMilestone(currentMilestone.id, index, updatedTask);

        const payload = {
            responses: task.responses || [],
            files: uploadedInviteFormImagesUrls?.map(file => ({
                url: file.file_url,
                name: file.name,
                mime_type: file.mime_type,
                size: file.size
            })) || [],
            response_by: newLoggedUserData.user.id,
        };
        dispatch(updateServiceRequestTask({ ticketId: id, taskId: task.id, payload, formName }));
    };

    const handleEdit = (index) => {
        // No need to do anything here since we're using shared state
        // The edit functionality is handled by the input components
    };


    const isTaskAlreadyUploaded = (task) => {
        if (task.input_type === 'FILE') {
            return task.files?.length > 0 && task.isSaved;
        }
        if (task.input_type === 'MULTI_SELECT') {
            return Array.isArray(task.responses) && task.responses.length > 0 && task.isSaved;
        }
        return task.isSaved;
    }


    const areAllTasksCompleted = useMemo(() => {
        if (!tasks.length || tasks.length === 0 || currentMilestone?.execution_flow_required == false) return true;
        return tasks.every(task => task.is_required && task.visibility_conditions.length === 0 ? isTaskAlreadyUploaded(task) : true);
    }, [tasks, currentMilestone?.execution_flow_required]);

    useEffect(() => {
        if (areAllTasksCompleted) {
            setHeaderColor((prev) => ({ ...prev, [currentMilestone?.id]: '#4ade80' }));
        } else {
            setHeaderColor((prev) => ({ ...prev, [currentMilestone?.id]: '#fb923c' }));
        }
    }, [areAllTasksCompleted, setHeaderColor, currentMilestone?.id]);

    const isSaveButtonDisabled = (task) => {
        if (task.input_type === 'FILE') {
            return !task.files?.length;
        }
        if (task.input_type === 'MULTI_SELECT') {
            return !task.responses?.length;
        }
        return !task.responses?.length;
    }

    const getSelectOptions = (options) => {
        return options?.map(option => ({
            value: option.value,
            label: option.label
        })) || [];
    }

    const getSelectedValues = (task) => {
        if (!task.responses || !task.options) return [];
        return task.responses.map(value => {
            const option = task.options.find(opt => opt.value === value);
            return option ? { value: option.value, label: option.label } : null;
        }).filter(Boolean);
    };

    // if (tasks.length === 0) {
    //     return (
    //         <motion.div
    //             initial={{ opacity: 0 }}
    //             animate={{ opacity: 1 }}
    //             exit={{ opacity: 0 }}
    //             className='flex items-center justify-center h-full'
    //         >
    //             <p className='text-sm text-gray-500'>No tasks available</p>
    //         </motion.div>
    //     );
    // };

    const customSelectStyles = {
        control: (provided) => ({
            ...provided,
            borderColor: '#D1D5DB',
            minHeight: '36px',
            boxShadow: 'none',
            '&:hover': {
                borderColor: '#9CA3AF'
            }
        }),
        valueContainer: (provided) => ({
            ...provided,
            padding: '0 8px'
        }),
        input: (provided) => ({
            ...provided,
            margin: '0px',
        }),
        indicatorSeparator: () => ({
            display: 'none'
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '36px'
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#4F46E5' : state.isFocused ? '#EEF2FF' : null,
            color: state.isSelected ? 'white' : '#374151',
            fontSize: '0.875rem',
            cursor: 'pointer'
        }),
        multiValue: (provided) => ({
            ...provided,
            backgroundColor: '#EEF2FF',
            borderRadius: '4px'
        }),
        multiValueLabel: (provided) => ({
            ...provided,
            color: '#4F46E5',
            fontSize: '0.75rem',
            padding: '2px 4px'
        }),
        multiValueRemove: (provided) => ({
            ...provided,
            color: '#4F46E5',
            '&:hover': {
                backgroundColor: '#E0E7FF',
                color: '#4338CA'
            }
        })
    };

    let raiseSignOffDisableReason = '';
    const isFEAssigned = memoizedServiceRequest?.agents?.find(agent => agent.role === TicketAgentRole.FE);
    let isEnabledRaiseSignoff = isFEAssigned;
    if (currentMilestone && currentMilestone.type === MilestoneType.RMA) {
        isEnabledRaiseSignoff = isFEAssigned && currentMilestone?.rma?.status === 'completed';
        if (currentMilestone?.rma?.status !== 'completed') {
            raiseSignOffDisableReason = 'Update RMA is Required';
        }
    }

    const showReachedSite = currentMilestone && checkInRange(currentMilestone.milestone_status, MilestoneSubStatus.NEW, MilestoneSubStatus.ENGINEER_AT_SITE) && currentMilestone?.agents?.find(agent => [TicketAgentRole.FE, TicketAgentRole.SCM].includes(agent.role) && agent.agent_id === newLoggedUserData.user.id);

    const showChecklist = currentMilestone && checkInRange(currentMilestone.milestone_status, MilestoneSubStatus.ENGINEER_AT_SITE) && currentMilestone?.agents?.find(agent => [TicketAgentRole.FE, TicketAgentRole.SCM].includes(agent.role) && agent.agent_id === newLoggedUserData.user.id);

    const signOffRaised = currentMilestone && checkInRange(currentMilestone.milestone_status, MilestoneSubStatus.SIGN_OFF_REQUESTED) && ![MilestoneSubStatus.SIGN_OFF_REJECTED, MilestoneSubStatus.CUSTOMER_SIGN_OFF_REJECTED].includes(currentMilestone.milestone_status);
    const isCurrentMilestonePav = isPavMilestone(currentMilestone);
    const hasCurrentPavAsset = isCurrentMilestonePav && hasPavMilestoneAssets(memoizedServiceRequest, currentMilestone);
    const canRaiseCurrentPavSignOff = isCurrentMilestonePav && canRaisePavMilestoneSignOff({
        ticket: memoizedServiceRequest,
        milestone: currentMilestone,
    });

    const canEditChecklist = currentMilestone?.milestone_status !== MilestoneSubStatus.HARD_CLOSED && memoizedServiceRequest?.status !== TicketStatus.CLOSED;

    if (isCurrentMilestonePav && !hasCurrentPavAsset) {
        raiseSignOffDisableReason = 'Add at least one asset to this PAV milestone.';
    } else if (!isCurrentMilestonePav && !isFEAssigned) {
        raiseSignOffDisableReason = 'Field Engineer must be assigned.';
    } else if (!isCurrentMilestonePav && !areAllTasksCompleted) {
        raiseSignOffDisableReason = 'All tasks must be completed.';
    };

    if (signOffRaised) {
        raiseSignOffDisableReason = 'Sign-off has already been raised.';
    }

    // PAV milestones without tasks use the asset-verification checklist.
    if (isCurrentMilestonePav && tasks.length === 0) {
        return (
            <PavAssetVerification
                key={currentMilestone.id}
                ticket={memoizedServiceRequest}
                milestone={currentMilestone}
                variant='chat'
                footerContent={showReachedSite && !signOffRaised ? (
                    <ReachedSite isFEAssigned={isFEAssigned} id={id} selectedMilestones={currentMilestone.id} />
                ) : null}
            />
        );
    }

    const isRaiseSignOffDisabled = isCurrentMilestonePav
        ? !canRaiseCurrentPavSignOff
        : !areAllTasksCompleted || signOffRaised || !isEnabledRaiseSignoff;

    return (
        <motion.div
            initial={{ x: '-100vw' }}
            animate={{ x: 0 }}
            exit={{ x: '100vw' }}
            className="h-full overflow-hidden bg-gray-50"
        >
            {currentMilestone?.execution_flow_required ? <Tasks isFEAssigned={isFEAssigned} tasks={tasks} isTaskAlreadyUploaded={isTaskAlreadyUploaded} canEditChecklist={canEditChecklist} handleTaskClick={handleTaskClick} handleEdit={handleEdit} setActiveTaskIndex={setActiveTaskIndex} handleFileUpload={handleFileUpload} /> : null}
            <div className="border-t bg-white">
                <RaiseSignOff
                    org_id={memoizedServiceRequest.org_id}
                    selectedMilestone={currentMilestone}
                    ticketId={id}
                    formName={formName}
                    className='my-2 px-4 flex items-center gap-2'
                    showRaised={false}
                    helpdesk_id={memoizedServiceRequest.helpdesk_id}
                >
                    <SimpleButton
                        disabled={isRaiseSignOffDisabled}
                        buttonIconComponent={
                            <span className="material-icons text-sm">assignment_turned_in</span>
                        }
                        btnClass={
                            `text-sm px-4 py-2 bg-scogoprimary text-white hover:bg-scogodarker rounded-md transition-all flex items-center gap-2 shadow-sm hover:shadow-md ${isRaiseSignOffDisabled ? 'opacity-50 cursor-not-allowed' : ''}`
                        }
                        textOrComponent={'Raise Sign Off'}
                        onHoverTitle={raiseSignOffDisableReason}
                    />
                </RaiseSignOff>
                {showReachedSite && !signOffRaised && <ReachedSite isFEAssigned={isFEAssigned} id={id} selectedMilestones={currentMilestone.id} />}
            </div>

            {activeTaskIndex !== null && tasks[activeTaskIndex].input_type !== 'FILE' && (
                <TaskInputBar
                    task={tasks[activeTaskIndex]}
                    index={activeTaskIndex}
                    handleSave={(index, task) => {
                        handleSave(index, task);
                        handleCloseInputBar();
                    }}
                    handleInputChange={handleInputChange}
                    handleFileUpload={handleFileUpload}
                    handleMultiSelectChange={handleMultiSelectChange}
                    getSelectOptions={getSelectOptions}
                    getSelectedValues={getSelectedValues}
                    customSelectStyles={customSelectStyles}
                    isSaveButtonDisabled={isSaveButtonDisabled}
                    handleCloseInputBar={handleCloseInputBar}
                />
            )}
        </motion.div>
    );
};


export default Checklist;


export const Tasks = ({ isFEAssigned, tasks, isTaskAlreadyUploaded, canEditChecklist, handleTaskClick, handleEdit, setActiveTaskIndex, handleFileUpload }) => {

    return (
        <div className="h-full flex flex-col" style={{ maxHeight: '40vh', overflowY: 'auto' }}>
            <div className="flex-1 overflow-y-auto">
                {tasks.map((task, index) => {

                    const isVisible = task.visibility_conditions.every(condition => {
                        const { task_id, operator, value } = condition;
                        const referencedTask = tasks.find(t => t.id === task_id);
                        if (!referencedTask) return true;

                        const responseValue = referencedTask.responses && referencedTask.responses.length > 0
                            ? referencedTask.responses.map(response => response?.value ? response.value : response)
                            : [];

                        switch (operator) {
                            case '=':
                                return responseValue.includes(value);
                            case '!=':
                                return !responseValue.includes(value);
                            case 'IN':
                                return Array.isArray(value) && responseValue.some(item => value.includes(item));
                            case 'NOT_IN':
                                return Array.isArray(value) && !responseValue.some(item => value.includes(item));
                            case 'LIKE':
                                return responseValue.some(item => String(item).includes(value));
                            case 'NOT_LIKE':
                                return !responseValue.some(item => String(item).includes(value));
                            case 'CONTAINS':
                                return responseValue.includes(value);
                            case 'NOT_CONTAINS':
                                return !responseValue.includes(value);
                            case 'EXISTS':
                                return responseValue.length > 0;
                            case 'NOT_EXISTS':
                                return responseValue.length === 0;
                            case 'REGEX':
                                return responseValue.some(item => new RegExp(value).test(String(item)));
                            case 'NOT_REGEX':
                                return !responseValue.some(item => new RegExp(value).test(String(item)));
                            case 'IS_NULL':
                                return responseValue.length === 0;
                            case 'IS_NOT_NULL':
                                return responseValue.length > 0;
                            default:
                                return true;
                        }
                    });

                    if (!isVisible) return null;

                    return <motion.div
                        key={task.id || index}
                        className={`cursor-pointer px-3 py-2 transition-colors ${index % 2 === 0 ? "bg-tagmsg hover:bg-red-100" : "bg-white-50 hover:bg-gray-200"}
                            }`}
                        onClick={() => handleTaskClick(index)}
                    >
                        <div className={`${isTaskAlreadyUploaded(task) ? '' : ' '}  p-2 flex justify-between items-center `}>
                            <div className="flex items-center justify-center gap-2 ">
                                {isTaskAlreadyUploaded(task) ?
                                    task.is_rejected ?
                                        <span class="material-icons text-base text-red-500">
                                            cancel
                                        </span>
                                        :
                                        <span class="material-icons text-base text-green-500">
                                            check_circle
                                        </span>
                                    :
                                    <span class="material-icons text-base text-gray-400">
                                        radio_button_unchecked
                                    </span>

                                }
                                <div className='w-full flex gap-2 items-center'>
                                    <p className="text-base max-h-max font-medium italic text-gray-500 leading-tight max-w-[95%]">{task.title}</p>
                                    {task.is_required ? <IconToolTip title='Required'><p className='text-scogoorange text-2xl'>*</p></IconToolTip> : null}
                                </div>
                                <>
                                    {task.files.some(file => file.is_duplicate) || task.is_duplicate ? (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200 mb-1">
                                            <span className="material-icons text-red-500 mr-0.5 text-xs">content_copy</span>
                                            Duplicate
                                        </span>
                                    ) : null}
                                </>
                            </div>
                            <div className={`flex items-center justify-center gap-2 ${!isFEAssigned ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                {task.input_type === 'FILE' && (
                                    <input
                                        multiple
                                        type="file"
                                        className="hidden"
                                        id={`file-input-${task.id || index}`}
                                        required={task.is_required}
                                        accept={task.allowed_file_types}
                                        onChange={(e) => handleFileUpload(index, e, task)}
                                        disabled={!isFEAssigned}
                                    />
                                )}

                                {(task.input_type === 'FILE' && !isTaskAlreadyUploaded(task)) && <span class="material-icons-outlined text-base text-gray-500">
                                    photo_camera
                                </span>}

                                {(task.input_type === 'TEXT' && !isTaskAlreadyUploaded(task)) && <span class="material-icons-outlined text-base text-gray-500">
                                    textsms
                                </span>}
                                {(task.input_type === "MULTI_SELECT" && !isTaskAlreadyUploaded(task)) && <span class="material-icons-outlined text-base text-gray-500">
                                    check_box
                                </span>}
                                {(task.input_type === "SELECT" && !isTaskAlreadyUploaded(task)) && <span class="material-icons-outlined text-base text-gray-500">
                                    check_circle
                                </span>}
                                {isTaskAlreadyUploaded(task) && (
                                    <motion.div
                                        className="text-sm text-gray-700 mt-1.5 ml-6"
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        {task.input_type === 'FILE' ? (
                                            <div className="flex flex-col gap-1.5">
                                                {task.files?.length > 0 ? (
                                                    <motion.button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            task.files.forEach((file) => {
                                                                window.open(file.url, '_blank');
                                                            });
                                                        }}
                                                        className="text-scogo99 hover:text-scogogray"
                                                        title="View all files"
                                                        whileHover={{ scale: 1.1 }}
                                                        whileTap={{ scale: 0.9 }}
                                                    >
                                                        <span className="material-icons-outlined  text-base">visibility</span>
                                                    </motion.button>
                                                ) : 'File uploaded'}

                                            </div>
                                        ) : task.input_type === 'MULTI_SELECT' ? (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                                {task.responses?.map((value, idx) => {
                                                    const option = task.options?.find(opt => opt.value === value.value);
                                                    return option ? (
                                                        <motion.span
                                                            key={idx}
                                                            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-sm font-normal bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-800 border border-indigo-100"
                                                            initial={{ scale: 0, opacity: 0 }}
                                                            animate={{ scale: 1, opacity: 1 }}
                                                            transition={{ delay: idx * 0.05 }}
                                                            whileHover={{ y: -1 }}
                                                        >
                                                            <span className="material-icons text-indigo-500 mr-0.5 text-base">check_circle</span>
                                                            {option.label}
                                                        </motion.span>
                                                    ) : null;
                                                })}
                                            </div>
                                        ) : (
                                            <motion.div
                                                className="flex items-center gap-1.5 w-full bg-gradient-to-r from-blue-50/50 to-indigo-50/50 p-1.5 rounded-md border border-indigo-100"
                                                initial={{ y: 3, opacity: 0 }}
                                                animate={{ y: 0, opacity: 1 }}
                                                transition={{ duration: 0.2 }}
                                                whileHover={{ boxShadow: "0 2px 4px rgba(99, 102, 241, 0.1)" }}
                                            >
                                                <span className="material-icons text-indigo-500 text-base">comment</span>
                                                <span className="break-all text-indigo-800 font-normal text-sm">
                                                    {task.responses?.[0]?.label}
                                                </span>
                                            </motion.div>
                                        )}
                                    </motion.div>
                                )}
                                {isTaskAlreadyUploaded(task) && canEditChecklist && (
                                    <motion.button
                                        className="text-scogo99 hover:text-scogogray rounded-full hover:bg-indigo-50 transition-all "
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (task.input_type === 'FILE') {
                                                const fileInput = document.getElementById(`file-input-${tasks[index].id || index}`);
                                                if (fileInput) {
                                                    fileInput.click();
                                                }
                                            }
                                            handleEdit(index);
                                            setActiveTaskIndex(index);
                                        }}
                                    >
                                        <span className="material-icons  mt-2 text-base">edit</span>
                                    </motion.button>
                                )}
                            </div>
                        </div>


                    </motion.div>
                })}
                {tasks.length === 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="flex flex-col items-center justify-center py-4 text-gray-500 bg-gradient-to-r from-blue-50/30 to-indigo-50/30 rounded-lg border border-indigo-100/50 shadow-sm"
                    >
                        <motion.span
                            className="material-icons text-4xl mb-2 text-indigo-400"
                            animate={{
                                y: [0, -5, 0],
                                opacity: [0.7, 1, 0.7]
                            }}
                            transition={{
                                duration: 2,
                                repeat: Infinity,
                                repeatType: "reverse"
                            }}
                        >
                            assignment
                        </motion.span>
                        <p className="text-indigo-700 font-bold text-sm">No tasks yet</p>
                        <p className="text-xs text-indigo-500 mt-0.5 max-w-xs text-center font-medium">Tasks will appear here when assigned</p>
                    </motion.div>
                )}
            </div>
        </div>
    )
};
